import { computed, inject, Injectable, Signal } from '@angular/core';
import { ResponseErrorListItemProps } from '@lentau/api/shared/monolith/types';
import { UserAuthorizeType } from '@lentau/auth/shared/enums';
import { Observable, filter, map } from 'rxjs';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AuthStepEnum } from '@lentau/auth/shared';
import { AuthStore } from './auth.store';
import { getErrorMessageFromErrorList } from '@lentau/api/shared/monolith/texting';
import { REQUEST_STATE } from '@lentau/ui-kit/core/enums/request-state.enum';

@Injectable({ providedIn: 'root' })
export class AuthFacade {
  private store$: AuthStore = inject(AuthStore);

  public currentPhoneNumber: Signal<string> = this.store$.currentPhoneNumber;

  public advCommunications: Signal<boolean> = this.store$.advCommunications;

  public lastPhoneNumber: Signal<string> = this.store$.lastPhoneNumber;

  public loading: Signal<boolean> = this.store$.loading;

  public isPreviouslyRegistered: Signal<boolean> = this.store$.isPreviouslyRegistered;

  public errorList: Signal<ResponseErrorListItemProps[]> = this.store$.errorList;

  public authErrorListMessage: Signal<string> = computed(() => getErrorMessageFromErrorList(this.store$.errorList()));

  public isAuth: Signal<boolean> = this.store$.isAuth;
  public isAuth$: Observable<boolean> = toObservable(this.isAuth);

  public isLoggedOut: Signal<boolean> = this.store$.isLoggedOut;
  public isLoggedOut$: Observable<boolean> = toObservable(this.isLoggedOut);

  public isLogouting: Signal<boolean> = this.store$.isLogouting;

  public sessionToken: Signal<string> = this.store$.sessionToken;
  public sessionToken$: Observable<string> = toObservable(this.sessionToken);

  public isSessionTokenInit$: Observable<boolean> = this.sessionToken$.pipe(
    map((token: string) => !!token),
    filter(Boolean)
  );
  public isSessionTokenInit: Signal<boolean> = toSignal(this.isSessionTokenInit$);

  public currentAuthStep: Signal<AuthStepEnum> = this.store$.currentAuthStep;
  public currentAuthStep$: Observable<AuthStepEnum> = toObservable(this.currentAuthStep);

  public authTokenChecked: Signal<boolean> = this.store$.authTokenChecked;
  public authTokenChecked$: Observable<boolean> = toObservable(this.authTokenChecked);

  public authTokenCheckInProgress: Signal<boolean> = this.store$.authTokenCheckInProgress;

  public userAuthorizeType: Signal<UserAuthorizeType | null> = this.store$.userAuthorizeType;
  public userAuthorizeType$: Observable<UserAuthorizeType | null> = toObservable(this.userAuthorizeType);

  public accessToken: Signal<string> = this.store$.accessToken;
  public refreshToken: Signal<string> = this.store$.refreshToken;
  public tokenExpiresIn: Signal<Date> = this.store$.tokenExpiresIn;

  public tokenRequestState: Signal<REQUEST_STATE> = this.store$.tokenRequestState;
  public tokenRequestState$: Observable<REQUEST_STATE> = toObservable(this.tokenRequestState);
  public tokenIsLoading: Signal<boolean> = computed(() => this.tokenRequestState() === REQUEST_STATE.LOADING);

  // GR-3599
  public callRequestState: Signal<REQUEST_STATE> = this.store$.callRequestState;
  public callRequestLoaded: Signal<boolean> = computed(() => this.store$.callRequestState() === REQUEST_STATE.SUCCESS);
  public callRequestFailed: Signal<boolean> = computed(() => this.store$.callRequestState() === REQUEST_STATE.FAIL);
  public callRequestStarted: Signal<boolean> = computed(() => this.store$.callRequestState() !== REQUEST_STATE.NONE);
  public callRequestLastPhoneNumber: Signal<string> = this.store$.callRequestLastPhoneNumber;
  public confirmationPhoneNumber: Signal<string> = this.store$.confirmationPhoneNumber;
  public qrCodeUri: Signal<string> = this.store$.qrCodeUri;

  // GR-2751
  public selectFlowActive: Signal<boolean> = this.store$.selectFlowActive;
  public selectAcceptRules: Signal<boolean> = this.store$.selectAcceptRules;
  public selectAcceptMarketing: Signal<boolean> = this.store$.selectAcceptMarketing;
  public suppressProfileSelection: Signal<boolean> = this.store$.suppressProfileSelection;

  public setAuthStep(currentAuthStep: AuthStepEnum): void {
    this.store$.setAuthStep(currentAuthStep);
  }

  public setAuthData(currentPhoneNumber: string, advCommunications: boolean): void {
    this.store$.setAuthData(currentPhoneNumber, advCommunications);
  }

  public getOtpCodePopup(lastPhoneNumber: string, acceptMarketingCommunications: boolean = true): void {
    this.store$.getOtpCodePopup(lastPhoneNumber, acceptMarketingCommunications);
  }

  public getOtpCodeB2B(lastPhoneNumber: string): void {
    this.store$.getOtpCodeB2B(lastPhoneNumber);
  }

  public getOtpCodeTablet(lastPhoneNumber: string, acceptMarketingCommunications: boolean): void {
    this.store$.getOtpCodeTablet(lastPhoneNumber, acceptMarketingCommunications);
  }

  public getOtpCodeReferral(lastPhoneNumber: string, acceptMarketingCommunications: boolean): void {
    this.store$.getOtpCodeReferral(lastPhoneNumber, acceptMarketingCommunications);
  }

  public logout(): void {
    this.store$.logout();
  }

  // Только для разлогина на фронте после разлогина на беке де-факто
  public logoutSuccess(): void {
    this.store$.logoutSuccess();
  }

  public authCodeTokenGetTablet(phone: string, code: string, isB2BRegistration: boolean = false): void {
    this.store$.authCodeTokenGet(phone, code, isB2BRegistration, true);
  }

  public authCodeTokenGetReferral(phone: string, code: string, isB2BRegistration: boolean = false): void {
    this.store$.authCodeTokenGet(phone, code, isB2BRegistration, false, true);
  }

  public authCodeTokenGet(phone: string, code: string, isB2BRegistration: boolean = false): void {
    this.store$.authCodeTokenGet(phone, code, isB2BRegistration);
  }

  public setUserAuthorizeType(userAuthorizeType: UserAuthorizeType): void {
    this.store$.setUserAuthorizeType(userAuthorizeType);
  }

  public resetPreviouslyRegistered(): void {
    this.store$.resetPreviouslyRegistered();
  }

  public setIsAuthByCache(): void {
    this.store$.setIsAuthByCache();
  }

  public getPassportGuestToken(): void {
    this.store$.getPassportGuestToken();
  }

  public refreshPassportToken(): void {
    this.store$.refreshPassportToken();
  }

  public setTokensData(refreshToken: string, accessToken: string, expiresIn: Date): void {
    this.store$.setTokensData(refreshToken, accessToken, expiresIn);
  }

  public sessionGet(): void {
    this.store$.sessionGet();
  }

  // GR-3599
  public callAuthStart(phoneNumber: string, acceptMarketingCommunications?: boolean): void {
    this.store$.callAuthStart(phoneNumber, acceptMarketingCommunications);
  }
  public authSessionStart(refresh: string, access: string, passportId: string): void {
    this.store$.authSessionStart(refresh, access, passportId);
  }
  public redirectFromCallToSms(): void {
    this.store$.redirectFromCallToSms();
  }

  // GR-2751
  public setSelectFlowActive(selectFlowActive: boolean): void {
    this.store$.setSelectFlowActive(selectFlowActive);
  }

  public setSelectConsent(acceptRules: boolean, acceptMarketing: boolean): void {
    this.store$.setSelectConsent(acceptRules, acceptMarketing);
  }

  public resetSelectFlow(): void {
    this.store$.resetSelectFlow();
  }

  public setSuppressProfileSelection(suppressProfileSelection: boolean): void {
    this.store$.setSuppressProfileSelection(suppressProfileSelection);
  }

  public applySocialSession(
    sessionId: string,
    accessToken: string,
    refreshToken: string,
    expiresInSec: number,
    isNewUser: boolean
  ): void {
    this.store$.applySocialSession(sessionId, accessToken, refreshToken, expiresInSec, isNewUser);
  }
}
